import { useState, useEffect, useMemo } from "react";
import Joi from "joi-browser";
import Form from "V2.0/components/Form";
import { putModelSettings } from "../../service";
import { message, Tooltip } from "antd";
import { Badge } from "V2.0/common/badge";
import {
  basicModels,
  modelIcons,
  premiumModels,
  proModels,
  previewModels,
  deprecatedModels,
  jarvisLabsBasicModels,
  jarvisLabsProModels,
  jarvisLabsPremiumModels,
} from "./gptModels";
import { FaCrown, FaStar } from "react-icons/fa";
import { maskGptResponseModel } from "./GPTResponsePremiumModel";
import { ExclamationTriangleIcon } from "@heroicons/react/24/solid";

function renderPreviewModel(label) {
  return (
    <Tooltip title="Occasionally inaccurate or include unnecessary text">
      <div className="flex gap-x-2 items-center">
        {label}
        <ExclamationTriangleIcon className="size-4 text-amber-500" />
      </div>
    </Tooltip>
  );
}

export function getModelOptions(models, options = {}) {
  return Object.keys(models).map((model) => {
    const Icon = modelIcons[model] ?? (() => null);
     const label = models[model];
    const isPreviewModel = !!previewModels[model];
    const isModelDeprecated = Boolean(deprecatedModels[model]);

    return {
      id: model,
      value: model,
      label: isPreviewModel ? renderPreviewModel(label) : label,
      icon: <Icon />,
      disabled: isModelDeprecated,
      ...options
    };
  });
}
const basicModelOptions = getModelOptions(basicModels);
const proModelOptions = getModelOptions(proModels);
const premiumModelOptions = getModelOptions(premiumModels);
const premiumModelDisabledOptions = getModelOptions(premiumModels, {
  disabled: true,
});
const jarvisLabsBasicOptions = getModelOptions(jarvisLabsBasicModels);
const jarvisLabsProOptions = getModelOptions(jarvisLabsProModels);
const jarvisLabsPremiumOptions = getModelOptions(jarvisLabsPremiumModels);
const jarvisLabsPremiumDisabledOptions = getModelOptions(
  jarvisLabsPremiumModels,
  { disabled: true }
);

// Jarvis Labs (India deployed) models are offered only when the assistant's audio data
// residency resolves to India. Otherwise only the assistant's current model is kept, so the
// picker isn't blank for an assistant already on one. (Inert in Catalyst today — see
// useIsIndiaResidency.ts; audioDataResidency doesn't exist anywhere here yet.)
function indiaDeployedOptions(options, showIndiaDeployedModels, currentModel) {
  return showIndiaDeployedModels
    ? options
    : options.filter((option) => option.value === currentModel);
}

const template = [
  {
    name: "temperature",
    label: "Temperature",
    type: "slider",
    min: 0,
    max: 1,
    step: 0.1,
  },

  {
    name: "maxOutputTokens",
    label: "Maximum Output token",
    type: "slider",
    min: 100,
    max: 1000,
  },
];

const buttonInfo = { label: "Save", style: "right-side" };

const schema = {
  gptResponseModel: Joi.string().required().label("Model"),
  maxOutputTokens: Joi.label("Maximum output tokens"),
  temperature: Joi.label("Temperature"),
};

const modelsMappings = {
  "gemini-2.5-flash-lite-preview-06-17": "gemini-2.5-flash-lite",
  "gemini-1.5-flash": "Gemini 1.5 Flash",
};

function GPTResponseModelSettings(props) {
  const {
    modelSettings,
    workspaceId,
    assistantId,
    syncAssistant,
    onSuccess,
    showPremiumOptions,
    enablePremiumOptions,
    showIndiaDeployedModels,
  } = props;
  const { gptResponseModel, temperature, maxOutputTokens } = modelSettings;

  const [inputField, setInputField] = useState({
    gptResponseModel: maskGptResponseModel(gptResponseModel),
    temperature,
    maxOutputTokens,
  });

  useEffect(() => {
    if (modelSettings) {
      const { gptResponseModel, temperature, maxOutputTokens } = modelSettings;

      const updatedGptResponseModel = modelsMappings[gptResponseModel];

      setInputField({
        gptResponseModel: Boolean(updatedGptResponseModel)
          ? maskGptResponseModel(updatedGptResponseModel)
          : maskGptResponseModel(gptResponseModel),
        temperature,
        maxOutputTokens,
      });
    }
  }, [modelSettings]);

  const [loading, setLoading] = useState(false);
  const [errorMessage] = useState({});

  const currentModel = inputField.gptResponseModel;

  const updatedTemplate = useMemo(() => {
    const premiumModelGroup = {
      // Was: label held the JSX directly (and was also used as the React `key` for this
      // section, below). Split into a plain-string `label` (used for the key / collapse-state
      // lookup) + `labelNode` (used for rendering) — ListboxWithSections already supports
      // labelNode (see ListBoxGrouped.tsx), so this also fixes a latent duplicate/odd-key risk
      // from using a JSX element as a key.
      label: "Premium",
      labelNode: (
        <>
          Premium{" "}
          <Badge color="amber">
            <FaCrown />
          </Badge>
        </>
      ),
      sticky: true,
      options: enablePremiumOptions
        ? [
            ...premiumModelOptions,
            ...indiaDeployedOptions(
              jarvisLabsPremiumOptions,
              showIndiaDeployedModels,
              currentModel
            ),
          ]
        : [
            ...premiumModelDisabledOptions,
            ...indiaDeployedOptions(
              jarvisLabsPremiumDisabledOptions,
              showIndiaDeployedModels,
              currentModel
            ),
          ],
    };

    const basicModelGroup = {
      label: "Basic",
      sticky: true,
      options: [
        ...basicModelOptions,
        ...indiaDeployedOptions(
          jarvisLabsBasicOptions,
          showIndiaDeployedModels,
          currentModel
        ),
      ],
    };

    const proModelGroup = {
      label: "Pro",
      labelNode: (
        <>
          Pro{" "}
          <Badge color="orange">
            <FaStar />
          </Badge>
        </>
      ),
      sticky: true,
      options: [
        ...proModelOptions,
        ...indiaDeployedOptions(
          jarvisLabsProOptions,
          showIndiaDeployedModels,
          currentModel
        ),
      ],
    };

    const modelTemplateForBasicAndProGroup = {
      name: "gptResponseModel",
      label: "Model",
      type: "grouped-select",
      options: [basicModelGroup, proModelGroup],
    };

    const modelTemplateForPremiumGroup = {
      name: "gptResponseModel",
      label: "Model",
      type: "grouped-select",
      options: [basicModelGroup, proModelGroup, premiumModelGroup],
    };

    if (showPremiumOptions) {
      return [modelTemplateForPremiumGroup, ...template];
    }

    return [modelTemplateForBasicAndProGroup, ...template];

  }, [showPremiumOptions, enablePremiumOptions, showIndiaDeployedModels, currentModel]);

  // const updatedTemplate = useMemo(() => {
  //   return [
  //     {
  //       name: "gptResponseModel",
  //       label: "Model",
  //       type: "select",
  //       options: [...basicModelOptions, ...proModelOptions]
  //     },
  //     ...template
  //   ]
  // }, []);

  const handleCreate = async (data) => {
    setLoading(true);

    try {
      const response = await putModelSettings({
        workspaceId,
        assistantId,
        payload: data,
      });
      if (response.status === 200) {
        message.success("Model updated");
        syncAssistant((prevAssistant) => {
          return {
            ...prevAssistant,
            modelSettings: response.data,
          };
        });
        setLoading(false);
        onSuccess();
      }
    } catch (e) {
      message.error(
        e.response.data?.msg ||
          e.response.data?.message ||
          e.response.data?.error ||
          "Failed to udpate model"
      );
      console.error("Error updating model settings");
      setLoading(false);
    }
  };

  return (
    <div>
      <Form
        key={JSON.stringify(inputField)}
        errorMessage={errorMessage}
        inputField={inputField}
        template={updatedTemplate}
        schema={schema}
        onSubmit={handleCreate}
        loading={loading}
        setLoading={setLoading}
        buttonInfo={buttonInfo}
      />
    </div>
  );
}

export { GPTResponseModelSettings };
